import React, { useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Pill, AlertCircle, Check } from "lucide-react";
import { getPgd, getDrug } from "@/lib/pharmacyFirst";
import type {
  DrugSelectionNode as DrugSelectionNodeType,
  DrugSelectionRule,
} from "@/lib/pharmacyFirst/types";

interface DrugSelectionNodeProps {
  node: DrugSelectionNodeType;
  isDarkMode?: boolean;
  context: Record<string, unknown>;
  pathwayPgdRefs: string[];
  stepNumber?: number;
  onAccept: (rule: DrugSelectionRule, ruleIndex: number) => void;
  onOverride: (pgdId: string, overrideReason: string, recommendedPgdId: string) => void;
  onSelect: (pgdId: string) => void;
}

function evalRule(rule: DrugSelectionRule, context: Record<string, unknown>): boolean {
  if (!rule.when) return false;
  return Object.entries(rule.when).every(([k, v]) => (context[k] as any) === v);
}

function pgdLabel(pgdId: string): string {
  try {
    const pgd = getPgd(pgdId);
    const drug = getDrug(pgd.drugRef);
    return `${drug.name} (PGD ${pgd.reference} v${pgd.version})`;
  } catch {
    return pgdId;
  }
}

function pgdDrugName(pgdId: string): string {
  try {
    const pgd = getPgd(pgdId);
    const drug = getDrug(pgd.drugRef);
    return drug.name;
  } catch {
    return pgdId;
  }
}

function pgdReference(pgdId: string): string {
  try {
    const pgd = getPgd(pgdId);
    return `PGD ${pgd.reference} v${pgd.version}`;
  } catch {
    return "";
  }
}

export function DrugSelectionNode({
  node,
  isDarkMode = false,
  context,
  pathwayPgdRefs,
  stepNumber,
  onAccept,
  onOverride,
  onSelect,
}: DrugSelectionNodeProps) {
  const matched = useMemo(() => {
    for (let i = 0; i < node.rules.length; i++) {
      if (evalRule(node.rules[i], context)) {
        return { rule: node.rules[i], index: i };
      }
    }
    return null;
  }, [node.rules, context]);

  const [overrideOpen, setOverrideOpen] = useState(false);
  const [overridePgdId, setOverridePgdId] = useState<string>("");
  const [overrideReason, setOverrideReason] = useState<string>("");
  const [selectedPgdId, setSelectedPgdId] = useState<string>("");

  const cardClass = `border rounded-2xl p-4 ${isDarkMode ? "border-gray-700 bg-gray-800" : "border-gray-200 bg-white"}`;

  // ── Equal-choice variant: radio buttons for all options ──────────────────
  if (node.variant === "equal_choice") {
    const choices = node.rules.map((r) => r.pgdId).filter((id): id is string => !!id);
    return (
      <div className={cardClass}>
        {stepNumber !== undefined && (
          <div className="flex items-center gap-3 mb-4">
            <div
              className={`flex items-center justify-center w-6 h-6 rounded-full text-sm font-semibold ${
                isDarkMode ? "bg-white text-black" : "bg-black text-white"
              }`}
            >
              {stepNumber}
            </div>
            <div className="flex-1">
              <h3 className={`text-sm font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}>
                Select antibiotic
              </h3>
              <p className={`text-xs mt-0.5 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                Both options have cleared pre-screening. Select the most appropriate drug for this patient.
              </p>
            </div>
          </div>
        )}

        <div className="space-y-2 mb-4">
          {choices.map((pgdId) => {
            const isSelected = selectedPgdId === pgdId;
            return (
              <button
                key={pgdId}
                type="button"
                onClick={() => setSelectedPgdId(pgdId)}
                className={`w-full text-left flex items-center gap-3 p-3 border rounded-lg transition-colors ${
                  isSelected
                    ? isDarkMode
                      ? "border-blue-500 bg-blue-950/40"
                      : "border-blue-500 bg-blue-50"
                    : isDarkMode
                      ? "border-gray-600 hover:bg-gray-700"
                      : "border-gray-200 hover:bg-gray-50"
                }`}
              >
                <span
                  className={`flex-shrink-0 w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                    isSelected
                      ? "border-blue-500 bg-blue-500"
                      : isDarkMode
                        ? "border-gray-500"
                        : "border-gray-400"
                  }`}
                >
                  {isSelected && <span className="w-1.5 h-1.5 rounded-full bg-white" />}
                </span>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium ${isDarkMode ? "text-white" : "text-gray-900"}`}>
                    {pgdDrugName(pgdId)}
                  </p>
                  <p className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}>
                    {pgdReference(pgdId)}
                  </p>
                </div>
                {isSelected && (
                  <Check className={`h-4 w-4 flex-shrink-0 ${isDarkMode ? "text-blue-400" : "text-blue-600"}`} />
                )}
              </button>
            );
          })}
        </div>

        <Button
          className="w-full"
          disabled={!selectedPgdId}
          onClick={() => onSelect(selectedPgdId)}
        >
          Continue with {selectedPgdId ? pgdDrugName(selectedPgdId) : "selected drug"}
        </Button>
      </div>
    );
  }

  // ── Recommend-with-override variant ──────────────────────────────────────
  if (!matched) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          No matching PGD recommendation could be made from the prior answers.
        </AlertDescription>
      </Alert>
    );
  }

  const recommendedPgdId = matched.rule.pgdId;
  if (!recommendedPgdId) {
    return (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          No matching PGD recommendation could be made from the prior answers.
        </AlertDescription>
      </Alert>
    );
  }
  const alternativePool = node.overridePgdRefs || pathwayPgdRefs;
  const alternatives = alternativePool.filter((p) => p !== recommendedPgdId);

  return (
    <div className={cardClass}>
      {stepNumber !== undefined && (
        <div className="flex items-center gap-3 mb-4">
          <div
            className={`flex items-center justify-center w-6 h-6 rounded-full text-sm font-semibold ${
              isDarkMode ? "bg-white text-black" : "bg-black text-white"
            }`}
          >
            {stepNumber}
          </div>
          <h3 className={`text-sm font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}>
            Drug selection
          </h3>
        </div>
      )}

      <div
        className={`border rounded-lg p-4 mb-4 ${
          isDarkMode ? "border-emerald-700 bg-emerald-950/40" : "border-emerald-200 bg-emerald-50"
        }`}
      >
        <div className="flex items-start gap-3">
          <Pill className={`h-5 w-5 mt-0.5 ${isDarkMode ? "text-emerald-400" : "text-emerald-700"}`} />
          <div className="flex-1">
            <p className={`text-sm font-semibold ${isDarkMode ? "text-emerald-200" : "text-emerald-900"}`}>
              Recommended: {pgdLabel(recommendedPgdId)}
            </p>
            <p className={`text-sm mt-1 ${isDarkMode ? "text-emerald-300" : "text-emerald-800"}`}>
              {matched.rule.recommendationText}
            </p>
          </div>
        </div>
      </div>

      {!overrideOpen ? (
        <div className="flex gap-2">
          <Button className="flex-1" onClick={() => onAccept(matched.rule, matched.index)}>
            Accept recommendation
          </Button>
          <Button
            variant="outline"
            onClick={() => setOverrideOpen(true)}
            disabled={alternatives.length === 0}
          >
            Override
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          <div>
            <label className={`text-xs font-medium block mb-1 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
              Alternative PGD
            </label>
            <Select value={overridePgdId} onValueChange={setOverridePgdId}>
              <SelectTrigger>
                <SelectValue placeholder="Select alternative PGD" />
              </SelectTrigger>
              <SelectContent>
                {alternatives.map((pid) => (
                  <SelectItem key={pid} value={pid}>
                    {pgdLabel(pid)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className={`text-xs font-medium block mb-1 ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
              Override reason (required)
            </label>
            <Select value={overrideReason} onValueChange={setOverrideReason}>
              <SelectTrigger>
                <SelectValue placeholder="Select reason" />
              </SelectTrigger>
              <SelectContent>
                {(node.overrideReasons || []).map((r) => (
                  <SelectItem key={r} value={r}>
                    {r}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setOverrideOpen(false);
                setOverridePgdId("");
                setOverrideReason("");
              }}
            >
              Cancel
            </Button>
            <Button
              className="flex-1"
              disabled={!overridePgdId || !overrideReason}
              onClick={() => onOverride(overridePgdId, overrideReason, recommendedPgdId)}
            >
              Confirm override
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
